import { scoreBg, scoreColor } from '@workspace/seedforge-shared'
import { cn } from '@workspace/ui/lib/utils'
import { CheckCircle2, ChevronDown, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { Prose } from '../../components/prose'
import type { MetricBreakdown } from '../types'

export { MetricBreakdownCard as EvaluationMetricBreakdown }

export function MetricBreakdownCard({ metric }: { metric: MetricBreakdown }) {
  const [open, setOpen] = useState(false)
  const scores = metric.judgeScores
  const hasJudges = scores.length > 0
  const spread = hasJudges
    ? Math.max(...scores.map((s) => s.score)) - Math.min(...scores.map((s) => s.score))
    : 0

  return (
    <div className="rounded-xl border border-border bg-card/30">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-4 rounded-t-xl px-5 py-4 transition-colors hover:bg-white/[0.02]"
      >
        {/* Score */}
        <span
          className={cn(
            'w-10 text-right text-xl font-bold tabular-nums',
            scoreColor(metric.collectiveScore)
          )}
        >
          {metric.collectiveScore}
        </span>

        {/* Label */}
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium text-foreground">{metric.label}</p>
        </div>

        {/* Mini judge score dots (only for deep evaluations) */}
        {hasJudges && (
          <div className="flex items-center gap-1.5">
            {scores.map((s) => (
              <div
                key={s.judgeKey}
                className="flex size-6 items-center justify-center rounded-full text-[9px] font-bold"
                style={{ backgroundColor: `${s.color}20`, color: s.color }}
                title={`${s.persona}: ${s.score}`}
              >
                {s.score}
              </div>
            ))}
          </div>
        )}

        {/* Spread indicator (only for deep evaluations) */}
        {hasJudges && (
          <div className="flex w-14 items-center justify-end gap-1">
            {spread >= 15 ? (
              <span className="text-[10px] font-medium text-amber-400">±{spread}</span>
            ) : (
              <span className="text-[10px] text-muted-foreground/80">±{spread}</span>
            )}
          </div>
        )}

        <ChevronDown
          className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="border-t border-border">
          {/* Full headline */}
          <div className="px-5 pt-4 pb-2">
            <Prose className="text-xs leading-relaxed text-muted-foreground">
              {metric.headline}
            </Prose>
          </div>

          {/* Score bar per judge (only for deep evaluations) */}
          {hasJudges && (
            <div className="px-5 py-4">
              <div className="space-y-2.5">
                {scores.map((s) => (
                  <div key={s.judgeKey} className="flex items-center gap-3">
                    <span
                      className="w-24 shrink-0 text-right text-xs font-medium"
                      style={{ color: s.color }}
                    >
                      {s.persona}
                    </span>
                    <div className="relative h-1.5 flex-1 rounded-full bg-input">
                      <div
                        className={cn('h-1.5 rounded-full', scoreBg(s.score))}
                        style={{ width: `${s.score}%` }}
                      />
                    </div>
                    <span
                      className={cn(
                        'w-7 text-right text-xs font-semibold tabular-nums',
                        scoreColor(s.score)
                      )}
                    >
                      {s.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Agreements / Strengths */}
          {metric.agreements.length > 0 && (
            <div className="border-t border-border px-5 py-3">
              <div className="mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="size-3 text-emerald-400" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-emerald-400/80">
                  {hasJudges ? 'Judges agree' : 'Strengths'}
                </span>
              </div>
              <ul className="space-y-1.5">
                {metric.agreements.map((a) => (
                  <li key={a} className="flex items-start gap-2">
                    <span className="mt-1.5 size-1 shrink-0 rounded-full bg-emerald-400" />
                    <Prose className="text-xs leading-relaxed text-muted-foreground">{a}</Prose>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disagreements / Concerns */}
          {metric.disagreements.length > 0 && (
            <div className="border-t border-border px-5 py-3">
              <div className="mb-2 flex items-center gap-1.5">
                <MessageSquare className="size-3 text-amber-400" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-amber-400/80">
                  {hasJudges ? 'Judges disagree' : 'Gaps'}
                </span>
              </div>
              <ul className="space-y-1.5">
                {metric.disagreements.map((d) => (
                  <li key={d} className="flex items-start gap-2">
                    <span className="mt-1.5 size-1 shrink-0 rounded-full bg-amber-400" />
                    <Prose className="text-xs leading-relaxed text-muted-foreground">{d}</Prose>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
